/**
 * Quality enforcement API client.
 * Handles real-time quality checks during survey execution.
 * Includes resilience features: retry with backoff, rate limiting, deduplication.
 */

import { apiClient } from './client';
import { detectDevice, getViewport, getScreen } from '../utils/device';

// ============================================================================
// Batch 4: Resilience - Retry, Rate Limiting, Deduplication
// ============================================================================

/**
 * Retry configuration.
 */
interface RetryConfig {
  maxRetries: number;
  baseDelayMs: number;
  maxDelayMs: number;
  backoffMultiplier: number;
}

const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  baseDelayMs: 500,
  maxDelayMs: 5000,
  backoffMultiplier: 2,
};

/**
 * Rate limiter state.
 */
interface RateLimiterState {
  tokens: number;
  lastRefill: number;
  maxTokens: number;
  refillRate: number; // tokens per second
}

// Rate limiter: 10 requests per second with burst of 15
const rateLimiter: RateLimiterState = {
  tokens: 15,
  lastRefill: Date.now(),
  maxTokens: 15,
  refillRate: 10,
};

/**
 * In-flight request deduplication cache.
 * Maps request key to pending promise.
 */
const inflightRequests = new Map<string, Promise<QualityCheckResponse>>();

/**
 * Recent request cache for deduplication (prevents duplicate calls within window).
 * Maps request key to { response, timestamp }.
 */
const recentRequests = new Map<string, { response: QualityCheckResponse; timestamp: number }>();
const DEDUP_WINDOW_MS = 2000; // 2 second deduplication window

/**
 * Calculate delay for exponential backoff with jitter.
 */
function calculateBackoffDelay(attempt: number, config: RetryConfig): number {
  const delay = Math.min(
    config.baseDelayMs * Math.pow(config.backoffMultiplier, attempt),
    config.maxDelayMs,
  );
  // Add jitter (±25%)
  const jitter = delay * 0.25 * (Math.random() * 2 - 1);
  return Math.round(delay + jitter);
}

/**
 * Sleep for specified milliseconds.
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Check if error is retryable (network errors, 5xx, 429).
 */
function isRetryableError(error: unknown): boolean {
  if (!error) return false;

  // Network errors
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return true;
  }

  // Axios error with response
  const axiosError = error as { response?: { status?: number }; code?: string };

  // Network error codes
  if (axiosError.code === 'ECONNABORTED' || axiosError.code === 'ETIMEDOUT') {
    return true;
  }

  // HTTP status codes
  const status = axiosError.response?.status;
  if (status) {
    // Retry on 429 (rate limited), 500, 502, 503, 504
    return status === 429 || (status >= 500 && status < 600);
  }

  return false;
}

/**
 * Refill rate limiter tokens based on elapsed time.
 */
function refillTokens(): void {
  const now = Date.now();
  const elapsed = (now - rateLimiter.lastRefill) / 1000;
  const tokensToAdd = elapsed * rateLimiter.refillRate;

  rateLimiter.tokens = Math.min(rateLimiter.maxTokens, rateLimiter.tokens + tokensToAdd);
  rateLimiter.lastRefill = now;
}

/**
 * Acquire a rate limit token. Returns true if allowed, false if rate limited.
 */
function acquireToken(): boolean {
  refillTokens();

  if (rateLimiter.tokens >= 1) {
    rateLimiter.tokens -= 1;
    return true;
  }

  return false;
}

/**
 * Wait for rate limit token to become available.
 */
async function waitForToken(maxWaitMs: number = 2000): Promise<boolean> {
  const startTime = Date.now();

  while (Date.now() - startTime < maxWaitMs) {
    if (acquireToken()) {
      return true;
    }
    // Wait a bit before checking again
    await sleep(100);
  }

  return false;
}

/**
 * Generate a cache key for request deduplication.
 */
function generateCacheKey(sessionId: string, eventType: string, questionCode?: string): string {
  return `${sessionId}:${eventType}:${questionCode || 'none'}`;
}

/**
 * Clean up expired entries from recent requests cache.
 */
function cleanupRecentRequests(): void {
  const now = Date.now();
  for (const [key, entry] of recentRequests.entries()) {
    if (now - entry.timestamp > DEDUP_WINDOW_MS) {
      recentRequests.delete(key);
    }
  }
}

/**
 * Execute request with retry logic.
 */
async function executeWithRetry<T>(
  fn: () => Promise<T>,
  config: RetryConfig = DEFAULT_RETRY_CONFIG,
): Promise<T> {
  let lastError: unknown;

  for (let attempt = 0; attempt <= config.maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      // Don't retry if not a retryable error
      if (!isRetryableError(error)) {
        throw error;
      }

      // Don't retry if this was the last attempt
      if (attempt === config.maxRetries) {
        break;
      }

      // Calculate backoff delay
      const delay = calculateBackoffDelay(attempt, config);
      console.warn(`Quality check attempt ${attempt + 1} failed, retrying in ${delay}ms...`);

      await sleep(delay);
    }
  }

  throw lastError;
}

/**
 * Quality action types.
 */
export type QualityAction = 'flag' | 'warn' | 'soft_terminate' | 'hard_terminate';

/**
 * Device context for quality checks.
 */
export interface DeviceContext {
  type?: 'mobile' | 'tablet' | 'desktop';
  os?: string;
  osVersion?: string;
  browser?: string;
  browserVersion?: string;
  isTouch?: boolean;
  viewportWidth?: number;
  viewportHeight?: number;
  screenWidth?: number;
  screenHeight?: number;
  pixelRatio?: number;
  timezone?: string;
  userAgent?: string;
}

/**
 * Real-time quality check request.
 */
export interface QualityCheckRequest {
  eventType: string;
  questionCode?: string;
  responseTimeSeconds?: number;
  response?: {
    questionCode: string;
    questionType: string;
    value: unknown;
    selectedOptions?: string[];
    matrixResponses?: Record<string, string>;
  };
  // Batch 2: Payload completeness
  surveyId?: string;
  qualityProfileId?: string;
  deviceContext?: DeviceContext;
  requestId?: string;
}

/**
 * Real-time quality check response.
 */
export interface QualityCheckResponse {
  /** Should continue or terminate */
  continue: boolean;
  /** Action to apply */
  action?: QualityAction;
  /** Warning message to show respondent */
  warningMessage?: string;
  /** Risk flags detected */
  riskFlags?: string[];
  /** Reason for action */
  reason?: string;
}

/**
 * Session start quality check request.
 */
export interface SessionStartCheckRequest {
  ipAddress?: string;
  fingerprintHash?: string;
  surveyId?: string;
  qualityProfileId?: string;
  deviceContext?: DeviceContext;
  requestId?: string;
}

/**
 * Quality context for enriching requests.
 */
export interface QualityContext {
  surveyId?: string;
  qualityProfileId?: string;
}

// Cached device context (computed once per page load)
let cachedDeviceContext: DeviceContext | null = null;

/**
 * Generate a unique request ID.
 */
export function generateRequestId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

/**
 * Build device context from browser environment.
 * Cached after first call for performance.
 */
export function buildDeviceContext(): DeviceContext {
  if (cachedDeviceContext) {
    return cachedDeviceContext;
  }

  try {
    const device = detectDevice();
    const viewport = getViewport();
    const screen = getScreen();

    cachedDeviceContext = {
      type: device.type,
      os: device.os,
      osVersion: device.osVersion,
      browser: device.browser,
      browserVersion: device.browserVersion,
      isTouch: device.isTouch,
      viewportWidth: viewport.width,
      viewportHeight: viewport.height,
      screenWidth: screen.width,
      screenHeight: screen.height,
      pixelRatio: window.devicePixelRatio || 1,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      userAgent: navigator.userAgent,
    };
  } catch {
    // Fallback if device detection fails
    cachedDeviceContext = {
      userAgent: navigator.userAgent,
    };
  }

  return cachedDeviceContext;
}

/**
 * Enrich request with device context and request ID.
 */
function enrichRequest<T extends object>(
  request: T,
  context?: QualityContext,
): T & { deviceContext: DeviceContext; requestId: string; surveyId?: string; qualityProfileId?: string } {
  return {
    ...request,
    deviceContext: buildDeviceContext(),
    requestId: generateRequestId(),
    surveyId: context?.surveyId,
    qualityProfileId: context?.qualityProfileId,
  };
}

/**
 * Perform quality check at session start (re-entry + IP baseline).
 * Uses retry with backoff and rate limiting.
 */
export async function checkSessionStart(
  sessionId: string,
  request: SessionStartCheckRequest,
  context?: QualityContext,
): Promise<QualityCheckResponse> {
  const cacheKey = generateCacheKey(sessionId, 'session.start');

  // Check for recent duplicate request
  cleanupRecentRequests();
  const cached = recentRequests.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < DEDUP_WINDOW_MS) {
    console.debug('Returning cached start check response');
    return cached.response;
  }

  // Check for in-flight request
  const inflight = inflightRequests.get(cacheKey);
  if (inflight) {
    console.debug('Waiting for in-flight start check request');
    return inflight;
  }

  // Wait for rate limit token
  const hasToken = await waitForToken();
  if (!hasToken) {
    console.warn('Rate limited - allowing continuation');
    return { continue: true };
  }

  // Create the request promise
  const requestPromise = (async (): Promise<QualityCheckResponse> => {
    try {
      const enrichedRequest = enrichRequest(request, context);

      const response = await executeWithRetry(async () => {
        const res = await apiClient.post<QualityCheckResponse>(
          `/api/v1/sessions/${sessionId}/quality/start-check`,
          enrichedRequest,
        );
        return res.data;
      });

      // Cache the response
      recentRequests.set(cacheKey, { response, timestamp: Date.now() });

      return response;
    } catch (error) {
      // On error, allow continuation (fail open for start check)
      console.error('Session start quality check failed after retries:', error);
      return { continue: true };
    } finally {
      inflightRequests.delete(cacheKey);
    }
  })();

  // Track in-flight request
  inflightRequests.set(cacheKey, requestPromise);

  return requestPromise;
}

/**
 * Perform real-time quality check.
 * Uses retry with backoff, rate limiting, and request deduplication.
 */
export async function checkQuality(
  sessionId: string,
  request: QualityCheckRequest,
  context?: QualityContext,
): Promise<QualityCheckResponse> {
  const cacheKey = generateCacheKey(sessionId, request.eventType, request.questionCode);

  // Check for recent duplicate request
  cleanupRecentRequests();
  const cached = recentRequests.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < DEDUP_WINDOW_MS) {
    console.debug(`Returning cached quality check response for ${request.eventType}`);
    return cached.response;
  }

  // Check for in-flight request
  const inflight = inflightRequests.get(cacheKey);
  if (inflight) {
    console.debug(`Waiting for in-flight quality check request for ${request.eventType}`);
    return inflight;
  }

  // Wait for rate limit token
  const hasToken = await waitForToken();
  if (!hasToken) {
    console.warn('Rate limited - allowing continuation');
    return { continue: true };
  }

  // Create the request promise
  const requestPromise = (async (): Promise<QualityCheckResponse> => {
    try {
      const enrichedRequest = enrichRequest(request, context);

      const response = await executeWithRetry(async () => {
        const res = await apiClient.post<QualityCheckResponse>(
          `/api/v1/sessions/${sessionId}/quality/check`,
          enrichedRequest,
        );
        return res.data;
      });

      // Cache the response
      recentRequests.set(cacheKey, { response, timestamp: Date.now() });

      return response;
    } catch (error) {
      // On error, allow continuation (fail open)
      console.error('Quality check failed after retries:', error);
      return { continue: true };
    } finally {
      inflightRequests.delete(cacheKey);
    }
  })();

  // Track in-flight request
  inflightRequests.set(cacheKey, requestPromise);

  return requestPromise;
}

/**
 * Quality check on answer change.
 */
export async function checkAnswerQuality(
  sessionId: string,
  questionCode: string,
  questionType: string,
  value: unknown,
  responseTimeSeconds: number,
  context?: QualityContext,
): Promise<QualityCheckResponse> {
  return checkQuality(
    sessionId,
    {
      eventType: 'answer.changed',
      questionCode,
      responseTimeSeconds,
      response: {
        questionCode,
        questionType,
        value,
        selectedOptions: Array.isArray(value) ? value : undefined,
      },
    },
    context,
  );
}

/**
 * Quality check on page submit.
 */
export async function checkPageQuality(
  sessionId: string,
  _pageNumber: number,
  totalTimeSeconds: number,
  context?: QualityContext,
): Promise<QualityCheckResponse> {
  return checkQuality(
    sessionId,
    {
      eventType: 'page.submitted',
      responseTimeSeconds: totalTimeSeconds,
    },
    context,
  );
}

/**
 * Quality check on survey completion.
 */
export async function checkCompletionQuality(
  sessionId: string,
  totalDurationSeconds: number,
  context?: QualityContext,
): Promise<QualityCheckResponse> {
  return checkQuality(
    sessionId,
    {
      eventType: 'session.completing',
      responseTimeSeconds: totalDurationSeconds,
    },
    context,
  );
}
